import React, { useState, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import supabase from '../supabase/supabase';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiX, FiCalendar, FiChevronDown, FiCopy, FiCheck, FiLogOut, FiSearch } = FiIcons;

const DAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

const PremiumSelect = ({ value, options, onChange, className = '', align = 'left' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [coords, setCoords] = useState({});
  const buttonRef = useRef(null);

  const handleOpen = () => {
    if (!isOpen) {
      const rect = buttonRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const openUp = spaceBelow < 220;
      setCoords({
        top: openUp ? undefined : rect.bottom + 6,
        bottom: openUp ? window.innerHeight - rect.top + 6 : undefined,
        left: align === 'left' ? undefined : rect.left,
        right: align === 'right' ? window.innerWidth - rect.right : undefined,
        width: rect.width
      });
    }
    setIsOpen(!isOpen);
  };

  return (
    <div className={`relative ${className}`}>
      <button
        ref={buttonRef}
        onClick={handleOpen}
        className="w-full bg-zinc-800/60 border border-white/5 hover:bg-zinc-700/60 rounded-lg px-3 py-2 text-sm text-white outline-none font-medium flex items-center justify-between transition-colors shadow-sm gap-3"
      >
        <span className="whitespace-nowrap">{value}</span>
        <SafeIcon icon={FiChevronDown} className="text-zinc-500 shrink-0" />
      </button>

      {isOpen && createPortal(
        <div className="fixed inset-0 z-[100]">
          <div className="absolute inset-0" onClick={() => setIsOpen(false)} />
          <div
            className="absolute z-[101] min-w-[120px] bg-[#18181B] border border-white/10 rounded-xl shadow-[0_10px_40px_-10px_rgba(0,0,0,0.8)] overflow-hidden py-1"
            style={{
              top: coords.top,
              bottom: coords.bottom,
              left: coords.left,
              right: coords.right,
              ...(align === 'left' ? { minWidth: Math.max(120, coords.width) } : {})
            }}
          >
            {options.map(opt => (
              <button
                key={opt}
                onClick={() => { onChange(opt); setIsOpen(false); }}
                className={`w-full text-left px-4 py-3 text-sm font-medium transition-colors whitespace-nowrap ${
                  value === opt ? 'bg-[#10B981]/10 text-[#10B981]' : 'text-zinc-300 hover:bg-white/5 hover:text-white'
                }`}
              >
                {opt}
              </button>
            ))}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

const PremiumTimezoneSelect = ({ value, options, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
      setSearch('');
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  const filteredOptions = useMemo(() => {
    if (!search) return options;
    const lower = search.toLowerCase();
    return options.filter(opt =>
      opt.toLowerCase().includes(lower) ||
      opt.replace(/_/g, ' ').toLowerCase().includes(lower)
    );
  }, [options, search]);

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className="w-full bg-zinc-800/40 border border-white/5 hover:border-white/10 rounded-xl pl-4 pr-10 py-3 text-sm text-white font-medium outline-none transition-all flex items-center justify-between text-left relative"
      >
        <span className="truncate">{value ? value.replace(/_/g, ' ') : 'Select Timezone'}</span>
        <div className="absolute right-4 pointer-events-none text-zinc-500 flex items-center justify-center">
          <SafeIcon icon={FiChevronDown} className="text-lg" />
        </div>
      </button>

      {isOpen && createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6" style={{ animation: 'tzFadeIn 0.2s ease-out' }}>
          <style>{`
            @keyframes tzFadeIn { from { opacity: 0; } to { opacity: 1; } }
            @keyframes tzSlideUpScale {
              from { opacity: 0; transform: translateY(10px) scale(0.98); }
              to { opacity: 1; transform: translateY(0) scale(1); }
            }
          `}</style>
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setIsOpen(false)} />
          <div
            className="relative w-full max-w-sm bg-zinc-900 border border-white/10 rounded-2xl shadow-[0_20px_60px_-15px_rgba(0,0,0,0.8)] overflow-hidden flex flex-col max-h-[70vh] md:max-h-[60vh]"
            style={{ animation: 'tzSlideUpScale 0.2s cubic-bezier(0.16, 1, 0.3, 1)' }}
          >
            <div className="p-4 border-b border-white/10 flex items-center gap-3 bg-zinc-900/80 backdrop-blur-md">
              <SafeIcon icon={FiSearch} className="text-zinc-400 text-lg shrink-0" />
              <input
                type="text"
                autoFocus
                placeholder="Search timezone..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full bg-transparent text-white placeholder:text-zinc-500 outline-none text-sm font-medium selection:bg-zinc-700"
              />
              <button
                onClick={() => setIsOpen(false)}
                className="w-8 h-8 rounded-full hover:bg-white/10 flex items-center justify-center text-zinc-400 transition-colors shrink-0"
              >
                <SafeIcon icon={FiX} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto no-scrollbar p-2 touch-pan-y">
              {filteredOptions.length > 0 ? (
                filteredOptions.map(tz => (
                  <button
                    key={tz}
                    onClick={() => { onChange(tz); setIsOpen(false); }}
                    className={`w-full text-left px-4 py-3 rounded-xl text-sm font-medium transition-colors mb-1 truncate ${
                      value === tz ? 'bg-[#10B981]/10 text-[#10B981]' : 'text-zinc-300 hover:bg-white/5 hover:text-white'
                    }`}
                  >
                    {tz.replace(/_/g, ' ')}
                  </button>
                ))
              ) : (
                <div className="px-4 py-12 text-center text-sm text-zinc-500 font-medium">No timezones found</div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

const PremiumTimePicker = ({ value = '09:00', onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [coords, setCoords] = useState({});
  const buttonRef = useRef(null);

  const [h24, m] = value.split(':');
  const isPm = parseInt(h24) >= 12;
  const h12 = parseInt(h24) % 12 || 12;

  const [hour, setHour] = useState(h12.toString().padStart(2, '0'));
  const [minute, setMinute] = useState(m || '00');
  const [ampm, setAmpm] = useState(isPm ? 'PM' : 'AM');

  useEffect(() => {
    const [h24Sync, mSync] = value.split(':');
    const isPmSync = parseInt(h24Sync) >= 12;
    const h12Sync = parseInt(h24Sync) % 12 || 12;
    setHour(h12Sync.toString().padStart(2, '0'));
    setMinute(mSync || '00');
    setAmpm(isPmSync ? 'PM' : 'AM');
  }, [value]);

  const hours = Array.from({ length: 12 }, (_, i) => (i + 1).toString().padStart(2, '0'));
  const minutes = Array.from({ length: 12 }, (_, i) => (i * 5).toString().padStart(2, '0'));
  const periods = ['AM', 'PM'];

  const triggerChange = (h, min, period) => {
    let hr = parseInt(h);
    if (period === 'PM' && hr < 12) hr += 12;
    if (period === 'AM' && hr === 12) hr = 0;
    onChange(`${hr.toString().padStart(2, '0')}:${min}`);
  };

  const handleOpen = () => {
    if (!isOpen) {
      const rect = buttonRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const openUp = spaceBelow < 250;
      setCoords({
        top: openUp ? undefined : rect.bottom + 8,
        bottom: openUp ? window.innerHeight - rect.top + 8 : undefined,
        right: window.innerWidth - rect.right
      });
    }
    setIsOpen(!isOpen);
  };

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        onClick={handleOpen}
        className="bg-zinc-800/60 border border-white/5 hover:bg-zinc-700/60 px-3 py-2 rounded-lg text-sm font-medium text-white transition-all flex items-center gap-1 shadow-sm"
      >
        <span>{hour}:{minute}</span>
        <span className="text-zinc-400 font-bold ml-0.5">{ampm}</span>
      </button>

      {isOpen && createPortal(
        <div className="fixed inset-0 z-[100]">
          <div className="absolute inset-0" onClick={() => setIsOpen(false)} />
          <div
            className="absolute z-[101] bg-[#18181B] border border-white/10 rounded-xl shadow-[0_10px_40px_-5px_rgba(0,0,0,0.8)] p-1.5 flex gap-1 h-[220px] w-[200px]"
            style={{ top: coords.top, bottom: coords.bottom, right: coords.right }}
          >
            <div className="flex-1 overflow-y-auto no-scrollbar flex flex-col gap-0.5 p-1 touch-pan-y">
              {hours.map(h => (
                <button key={h} onClick={() => { setHour(h); triggerChange(h, minute, ampm); }}
                  className={`w-full py-2.5 text-sm rounded-md font-medium transition-colors ${h === hour ? 'bg-zinc-700 text-white shadow-sm' : 'text-zinc-500 hover:text-white hover:bg-zinc-800/50'}`}>
                  {h}
                </button>
              ))}
            </div>
            <div className="flex-1 overflow-y-auto no-scrollbar flex flex-col gap-0.5 p-1 border-l border-white/5 touch-pan-y">
              {minutes.map(min => (
                <button key={min} onClick={() => { setMinute(min); triggerChange(hour, min, ampm); }}
                  className={`w-full py-2.5 text-sm rounded-md font-medium transition-colors ${min === minute ? 'bg-zinc-700 text-white shadow-sm' : 'text-zinc-500 hover:text-white hover:bg-zinc-800/50'}`}>
                  {min}
                </button>
              ))}
            </div>
            <div className="flex-1 flex flex-col gap-0.5 p-1 border-l border-white/5 touch-pan-y">
              {periods.map(p => (
                <button key={p} onClick={() => { setAmpm(p); triggerChange(hour, minute, p); }}
                  className={`w-full py-2.5 text-sm rounded-md font-medium transition-colors ${p === ampm ? 'bg-zinc-700 text-white shadow-sm' : 'text-zinc-500 hover:text-white hover:bg-zinc-800/50'}`}>
                  {p}
                </button>
              ))}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

const CopyRow = ({ label, path, linkTo, disabled }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    if (disabled) return;
    navigator.clipboard.writeText(path);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="flex items-center justify-between bg-zinc-800/40 rounded-xl px-4 py-3">
      <span className={`text-sm font-medium ${disabled ? 'text-zinc-500' : 'text-zinc-200'}`}>
        {linkTo && !disabled ? (
          <a href={linkTo} target="_blank" rel="noreferrer"
            className="hover:text-white underline decoration-white/30 underline-offset-4 transition-colors">
            {label}
          </a>
        ) : label}
      </span>
      <div className="flex items-center gap-1.5">
        <input type="text" readOnly value={path}
          className="bg-transparent text-right text-zinc-500 font-mono text-sm w-32 outline-none cursor-default selection:bg-zinc-600 selection:text-white" />
        <button onClick={handleCopy} disabled={disabled}
          className={`relative flex items-center justify-center transition-colors p-1.5 rounded-md ${disabled ? 'text-zinc-700 cursor-not-allowed' : 'text-zinc-500 hover:text-white hover:bg-white/5'}`}
          title="Copy Link">
          <SafeIcon icon={copied ? FiCheck : FiCopy} className={`text-base transition-colors ${copied ? 'text-[#10B981]' : ''}`} />
          {copied && (
            <span className="absolute -top-8 left-1/2 -translate-x-1/2 bg-zinc-800 text-white text-[10px] font-bold px-2.5 py-1 rounded-md shadow-lg whitespace-nowrap z-50 pointer-events-none">
              Copied!
            </span>
          )}
        </button>
      </div>
    </div>
  );
};

export default function SettingsPanel({ isOpen, onClose, settings, updateSetting, updateMultipleSettings, userId, onConnectCalendar }) {
  const navigate = useNavigate();

  const [localStudioName, setLocalStudioName] = useState(settings?.studio_name || '');
  const isEditingName = useRef(false);
  const isDirty = useRef(false);
  const [saveStatus, setSaveStatus] = useState('idle');
  const [showDisconnectModal, setShowDisconnectModal] = useState(false);

  const [keywordInput, setKeywordInput] = useState('');

  const [forceCustomLeadIn, setForceCustomLeadIn] = useState(false);
  const [forceCustomWrapUp, setForceCustomWrapUp] = useState(false);

  const [currentUserEmail, setCurrentUserEmail] = useState('');
  const [resetStatus, setResetStatus] = useState('idle');
  const [isUpdatingEmail, setIsUpdatingEmail] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [emailUpdateStatus, setEmailUpdateStatus] = useState('idle');

  const bufferOptions = ['None', '15 Min', '30 Min', '1 Hour', 'Custom'];

  const bufferToUi = (val) => {
    if (val === 0 || val === undefined || val === null) return 'None';
    if (val === 15) return '15 Min';
    if (val === 30) return '30 Min';
    if (val === 60) return '1 Hour';
    return 'Custom';
  };

  const uiToBuffer = (ui) => {
    if (ui === 'None') return 0;
    if (ui === '15 Min') return 15;
    if (ui === '30 Min') return 30;
    if (ui === '1 Hour') return 60;
    return null;
  };

  const [leadInCustom, setLeadInCustom] = useState('');
  const [wrapUpCustom, setWrapUpCustom] = useState('');

  const timezones = useMemo(() => {
    try { return Intl.supportedValuesOf('timeZone'); }
    catch (e) { return [Intl.DateTimeFormat().resolvedOptions().timeZone]; }
  }, []);

  useEffect(() => {
    let mounted = true;
    supabase.auth.getUser().then(({ data }) => {
      if (mounted && data?.user) setCurrentUserEmail(data.user.email);
    });
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    if (!isEditingName.current && settings?.studio_name !== undefined && settings.studio_name !== localStudioName) {
      setLocalStudioName(settings.studio_name);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings?.studio_name]);

  useEffect(() => {
    if (settings?.lead_in_buffer !== undefined && ![0, 15, 30, 60].includes(settings.lead_in_buffer)) {
      setLeadInCustom(settings.lead_in_buffer.toString());
    }
  }, [settings?.lead_in_buffer]);

  useEffect(() => {
    if (settings?.wrap_up_buffer !== undefined && ![0, 15, 30, 60].includes(settings.wrap_up_buffer)) {
      setWrapUpCustom(settings.wrap_up_buffer.toString());
    }
  }, [settings?.wrap_up_buffer]);

  useEffect(() => {
    if (!isDirty.current) return;
    const timer = setTimeout(async () => {
      if (localStudioName !== settings?.studio_name) {
        const success = await updateSetting('studio_name', localStudioName);
        if (success) {
          setSaveStatus('saved');
          setTimeout(() => setSaveStatus('idle'), 1500);
          isDirty.current = false;
        }
      }
    }, 500);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localStudioName]);

  const handleNameChange = (e) => {
    isDirty.current = true;
    setLocalStudioName(e.target.value);
  };

  const toggleDay = (idx) => {
    const currentDays = settings?.operating_days || [1, 2, 3, 4, 5];
    const newDays = currentDays.includes(idx)
      ? currentDays.filter(d => d !== idx)
      : [...currentDays, idx].sort();
    updateSetting('operating_days', newDays);
  };

  const handleLeadInChange = (uiVal) => {
    if (uiVal === 'Custom') { setForceCustomLeadIn(true); setLeadInCustom(''); }
    else { setForceCustomLeadIn(false); updateSetting('lead_in_buffer', uiToBuffer(uiVal)); }
  };

  const handleWrapUpChange = (uiVal) => {
    if (uiVal === 'Custom') { setForceCustomWrapUp(true); setWrapUpCustom(''); }
    else { setForceCustomWrapUp(false); updateSetting('wrap_up_buffer', uiToBuffer(uiVal)); }
  };

  const handleCustomBufferBlur = (key, customVal, setForceMode) => {
    if (customVal === '') { setForceMode(false); updateSetting(key, 0); return; }
    const parsed = parseInt(customVal);
    if (!isNaN(parsed) && parsed >= 0) { setForceMode(false); updateSetting(key, parsed); }
    else { setForceMode(false); updateSetting(key, 0); }
  };

  const handleCustomBufferKeyDown = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); e.target.blur(); }
  };

  const handleKeywordKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const val = keywordInput.trim();
      if (val) {
        const current = settings?.tracked_keywords || [];
        if (!current.includes(val)) updateSetting('tracked_keywords', [...current, val]);
        setKeywordInput('');
      }
    }
  };

  const removeKeyword = (kw) => {
    const current = settings?.tracked_keywords || [];
    updateSetting('tracked_keywords', current.filter(k => k !== kw));
  };

  const handleSignOut = async () => {
    try { await supabase.auth.signOut(); navigate('/login'); }
    catch (error) { console.error('Error signing out:', error); }
  };

  const handlePasswordReset = async () => {
    if (!currentUserEmail) return;
    setResetStatus('loading');
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(currentUserEmail, {
        redirectTo: `${window.location.origin}/`
      });
      if (error) throw error;
      setResetStatus('success');
      setTimeout(() => setResetStatus('idle'), 3000);
    } catch (error) {
      console.error(error);
      setResetStatus('error');
      setTimeout(() => setResetStatus('idle'), 3000);
    }
  };

  const handleEmailUpdate = async (e) => {
    e.preventDefault();
    if (!newEmail || newEmail === currentUserEmail) return;
    setEmailUpdateStatus('loading');
    try {
      const { error } = await supabase.auth.updateUser(
        { email: newEmail },
        { emailRedirectTo: `${window.location.origin}/` }
      );
      if (error) throw error;
      setEmailUpdateStatus('success');
      setTimeout(() => {
        setEmailUpdateStatus('idle');
        setIsUpdatingEmail(false);
        setNewEmail('');
      }, 5000);
    } catch (error) {
      console.error(error);
      setEmailUpdateStatus('error');
      setTimeout(() => setEmailUpdateStatus('idle'), 3000);
    }
  };

  const leadInUiValue = forceCustomLeadIn ? 'Custom' : bufferToUi(settings?.lead_in_buffer);
  const wrapUpUiValue = forceCustomWrapUp ? 'Custom' : bufferToUi(settings?.wrap_up_buffer);

  return (
    <>
      <div
        className={`fixed inset-0 bg-black/40 backdrop-blur-sm z-40 transition-opacity duration-300 ${
          isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
      />

      <div
        className={`fixed top-0 right-0 h-screen w-full max-w-md bg-zinc-950/95 backdrop-blur-xl border-l border-white/10 z-50 transform transition-transform duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] flex flex-col ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex items-center justify-between p-6 border-b border-white/5 flex-shrink-0">
          <h2 className="text-lg font-bold text-white tracking-tight">Studio Settings</h2>
          <button onClick={onClose}
            className="w-8 h-8 rounded-full bg-zinc-800/50 border border-white/5 flex items-center justify-center text-zinc-400 hover:text-white hover:bg-zinc-700 transition-colors">
            <SafeIcon icon={FiX} className="text-lg" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 flex flex-col gap-6 no-scrollbar relative">

          {/* Calendar & Auto-Pilot Block */}
          <div className="bg-zinc-900 rounded-2xl border border-white/5 p-4 flex flex-col gap-4 relative">
            <div className="flex flex-col gap-3">
              <span className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider pl-1">Calendar</span>
              {settings?.is_calendar_connected ? (
                <div className="flex items-center justify-between bg-zinc-800/40 rounded-xl px-4 py-3 gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-2 h-2 rounded-full bg-[#10B981] animate-pulse shadow-[0_0_8px_rgba(16,185,129,0.5)] flex-shrink-0"></div>
                    <span className="text-sm font-medium text-zinc-200 truncate" title={settings?.google_connected_email}>
                      {settings?.google_connected_email || 'Connected'}
                    </span>
                  </div>
                  <button
                    onClick={() => setShowDisconnectModal(true)}
                    className="text-xs font-bold text-red-500 hover:text-red-400 transition-colors tracking-tight flex-shrink-0 outline-none"
                  >
                    DISCONNECT
                  </button>
                </div>
              ) : (
                <button
                  onClick={onConnectCalendar}
                  className="w-full py-3 rounded-xl font-bold tracking-tight text-black bg-white hover:bg-zinc-200 transition-colors text-sm flex items-center justify-center gap-2"
                >
                  <SafeIcon icon={FiCalendar} className="text-lg" />
                  CONNECT GOOGLE CALENDAR
                </button>
              )}
            </div>

            <div className="flex flex-col gap-3 pt-2 border-t border-white/5 mt-1">
              <div className="flex flex-col">
                <span className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider pl-1">Booking Names</span>
                <p className="text-xs text-zinc-400 pl-1 mt-1 leading-relaxed">
                  Type exactly what you call your sessions (e.g., Podcast, Video Shoot) and press Enter.
                </p>
              </div>
              <div
                className="bg-zinc-800/40 border border-white/5 rounded-xl p-3 min-h-[52px] flex flex-col justify-center transition-colors focus-within:border-white/10 focus-within:bg-zinc-800/60 cursor-text"
                onClick={() => document.getElementById('keyword-input')?.focus()}
              >
                <div className="flex flex-wrap gap-2 items-center">
                  {(settings?.tracked_keywords || []).map(kw => (
                    <span key={kw} className="bg-zinc-700 text-white text-xs font-medium px-2.5 py-1.5 rounded-lg flex items-center gap-1.5 shadow-sm select-none">
                      {kw}
                      <button onClick={(e) => { e.stopPropagation(); removeKeyword(kw); }} className="text-zinc-400 hover:text-white transition-colors">
                        <SafeIcon icon={FiX} className="text-sm" />
                      </button>
                    </span>
                  ))}
                  <input
                    id="keyword-input"
                    type="text"
                    value={keywordInput}
                    onChange={(e) => setKeywordInput(e.target.value)}
                    onKeyDown={handleKeywordKeyDown}
                    placeholder={(settings?.tracked_keywords || []).length === 0 ? "e.g. Podcast" : "Add another..."}
                    className="bg-transparent text-sm text-white outline-none flex-1 min-w-[100px] placeholder:text-zinc-600 font-medium selection:bg-zinc-600"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="bg-zinc-900 rounded-2xl border border-white/5 p-4 flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <span className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider pl-1 mb-0.5">Studio Name</span>
              <div className="relative flex items-center">
                <input
                  type="text"
                  placeholder="Studio Name"
                  value={localStudioName}
                  onFocus={() => isEditingName.current = true}
                  onBlur={() => {
                    isEditingName.current = false;
                    if (isDirty.current && settings && localStudioName !== settings.studio_name) {
                      updateSetting('studio_name', localStudioName).then(success => {
                        if (success) {
                          setSaveStatus('saved');
                          setTimeout(() => setSaveStatus('idle'), 1500);
                          isDirty.current = false;
                        }
                      });
                    }
                  }}
                  onChange={handleNameChange}
                  className="w-full bg-zinc-800/40 border border-white/5 hover:border-white/10 rounded-xl pl-4 pr-10 py-3 text-sm text-white focus:border-zinc-500 focus:bg-zinc-800/80 outline-none transition-all placeholder:text-zinc-600 selection:bg-zinc-600 selection:text-white"
                />
                <div className={`absolute right-3 flex items-center justify-center pointer-events-none transition-opacity duration-500 ${saveStatus === 'saved' ? 'opacity-100' : 'opacity-0'}`}>
                  <SafeIcon icon={FiCheck} className="text-green-500/70 w-5 h-5" />
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between bg-zinc-800/40 rounded-xl px-4 py-3">
                <span className="text-sm font-medium text-zinc-200">Show Name on Displays</span>
                <button
                  onClick={() => updateSetting('show_name', !settings?.show_name)}
                  className={`relative w-11 h-6 rounded-full transition-colors duration-300 outline-none flex-shrink-0 ${settings?.show_name ? 'bg-[#10B981]' : 'bg-zinc-700'}`}
                >
                  <span className={`absolute top-1 left-1 bg-white w-4 h-4 rounded-full transition-transform duration-300 shadow-sm ${settings?.show_name ? 'translate-x-5' : 'translate-x-0'}`} />
                </button>
              </div>
              <div className="flex items-center justify-between bg-zinc-800/40 rounded-xl px-4 py-3">
                <span className="text-sm font-medium text-zinc-200">Show Clock on Displays</span>
                <button
                  onClick={() => updateSetting('show_clock', !settings?.show_clock)}
                  className={`relative w-11 h-6 rounded-full transition-colors duration-300 outline-none flex-shrink-0 ${settings?.show_clock ? 'bg-[#10B981]' : 'bg-zinc-700'}`}
                >
                  <span className={`absolute top-1 left-1 bg-white w-4 h-4 rounded-full transition-transform duration-300 shadow-sm ${settings?.show_clock ? 'translate-x-5' : 'translate-x-0'}`} />
                </button>
              </div>
            </div>
          </div>

          <div className="bg-zinc-900 rounded-2xl border border-white/5 p-4 flex flex-col gap-6">
            <div className="flex flex-col gap-2">
              <span className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider pl-1">Timezone</span>
              <PremiumTimezoneSelect
                value={settings?.timezone || ''}
                options={timezones}
                onChange={(val) => updateSetting('timezone', val)}
              />
            </div>

            <p className="text-sm font-medium text-zinc-400 leading-relaxed tracking-tight">
              Auto-Pilot will set the Main Door to CLOSED outside these hours.
            </p>

            <div className="flex flex-col gap-2">
              <span className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider pl-1">Operating Days</span>
              <div className="flex items-center justify-between bg-zinc-800/40 rounded-xl p-2">
                {DAYS.map((day, idx) => {
                  const isActive = (settings?.operating_days || [1, 2, 3, 4, 5]).includes(idx);
                  return (
                    <button key={idx} onClick={() => toggleDay(idx)}
                      className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                        isActive ? 'bg-white text-black shadow-sm' : 'text-zinc-500 hover:bg-zinc-700/50 hover:text-zinc-300'
                      }`}>
                      {day}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <span className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider pl-1">Operating Hours</span>
              <div className="flex items-center justify-between bg-zinc-800/40 rounded-xl px-4 py-3">
                <span className="text-sm font-medium text-zinc-200">Opening Time</span>
                <PremiumTimePicker value={settings?.open_time || '09:00'} onChange={(val) => updateSetting('open_time', val)} />
              </div>
              <div className="flex items-center justify-between bg-zinc-800/40 rounded-xl px-4 py-3">
                <span className="text-sm font-medium text-zinc-200">Closing Time</span>
                <PremiumTimePicker value={settings?.close_time || '17:00'} onChange={(val) => updateSetting('close_time', val)} />
              </div>
            </div>
          </div>

          <div className="bg-zinc-900 rounded-2xl border border-white/5 p-4">
            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between bg-zinc-800/40 rounded-xl px-4 py-3 min-h-[60px]">
                <span className="text-sm font-medium text-zinc-200">Lead-In Buffer</span>
                <div className="flex items-center gap-2">
                  {leadInUiValue === 'Custom' && (
                    <input type="number" placeholder="MIN" autoFocus value={leadInCustom}
                      onChange={(e) => setLeadInCustom(e.target.value)}
                      onBlur={() => handleCustomBufferBlur('lead_in_buffer', leadInCustom, setForceCustomLeadIn)}
                      onKeyDown={handleCustomBufferKeyDown}
                      className="w-16 bg-zinc-800/60 border border-white/5 rounded-lg px-2 py-2 text-sm text-center text-white outline-none focus:border-zinc-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none placeholder:text-zinc-600 selection:bg-zinc-600 selection:text-white"
                    />
                  )}
                  <PremiumSelect value={leadInUiValue} options={bufferOptions} onChange={handleLeadInChange} align="right" />
                </div>
              </div>

              <div className="flex items-center justify-between bg-zinc-800/40 rounded-xl px-4 py-3 min-h-[60px]">
                <span className="text-sm font-medium text-zinc-200">Wrap-Up Buffer</span>
                <div className="flex items-center gap-2">
                  {wrapUpUiValue === 'Custom' && (
                    <input type="number" placeholder="MIN" autoFocus value={wrapUpCustom}
                      onChange={(e) => setWrapUpCustom(e.target.value)}
                      onBlur={() => handleCustomBufferBlur('wrap_up_buffer', wrapUpCustom, setForceCustomWrapUp)}
                      onKeyDown={handleCustomBufferKeyDown}
                      className="w-16 bg-zinc-800/60 border border-white/5 rounded-lg px-2 py-2 text-sm text-center text-white outline-none focus:border-zinc-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none placeholder:text-zinc-600 selection:bg-zinc-600 selection:text-white"
                    />
                  )}
                  <PremiumSelect value={wrapUpUiValue} options={bufferOptions} onChange={handleWrapUpChange} align="right" />
                </div>
              </div>
            </div>
          </div>

          <div className="bg-zinc-900 rounded-2xl border border-white/5 p-4 flex flex-col gap-2">
            <span className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider pl-1 mb-1">Display Links</span>
            {userId ? (
              <>
                <CopyRow label="Main Door" path={`${window.location.origin}/display/main?s=${userId}`} linkTo={`/display/main?s=${userId}`} />
                <CopyRow label="Lobby" path={`${window.location.origin}/display/lobby?s=${userId}`} linkTo={`/display/lobby?s=${userId}`} />
                <CopyRow label="Notice" path={`${window.location.origin}/display/notice?s=${userId}`} linkTo={`/display/notice?s=${userId}`} />
              </>
            ) : (
              <>
                <CopyRow label="Main Door" path="Loading..." disabled />
                <CopyRow label="Lobby" path="Loading..." disabled />
                <CopyRow label="Notice" path="Loading..." disabled />
              </>
            )}
          </div>

          <div className="bg-zinc-900 rounded-2xl border border-white/5 p-4 flex flex-col gap-4">
            <div className="flex flex-col gap-1">
              <span className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider pl-1">Account Security</span>
              {currentUserEmail && (
                <span className="text-xs text-zinc-400 pl-1 tracking-tight">Logged in as: {currentUserEmail}</span>
              )}
            </div>

            <div className="flex flex-col gap-2">
              <button onClick={handlePasswordReset} disabled={resetStatus !== 'idle'}
                className="w-full flex items-center justify-between bg-zinc-800/40 hover:bg-zinc-700/50 border border-white/5 rounded-xl px-4 py-3 transition-colors outline-none">
                <span className="text-sm font-medium text-zinc-200">Send Password Reset</span>
                {resetStatus === 'success' && <span className="text-xs font-bold text-[#10B981]">Sent!</span>}
                {resetStatus === 'error' && <span className="text-xs font-bold text-red-400">Error</span>}
              </button>

              {!isUpdatingEmail ? (
                <button onClick={() => setIsUpdatingEmail(true)}
                  className="w-full flex items-center justify-between bg-zinc-800/40 hover:bg-zinc-700/50 border border-white/5 rounded-xl px-4 py-3 transition-colors outline-none">
                  <span className="text-sm font-medium text-zinc-200">Update Email Address</span>
                </button>
              ) : (
                <form onSubmit={handleEmailUpdate} className="flex flex-col gap-3 bg-zinc-800/40 border border-white/5 rounded-xl p-3">
                  <input type="email" placeholder="New email address" value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)} required autoFocus
                    disabled={emailUpdateStatus === 'loading' || emailUpdateStatus === 'success'}
                    className="w-full bg-zinc-900 border border-white/5 hover:border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:border-zinc-500 outline-none transition-all placeholder:text-zinc-600 disabled:opacity-50"
                  />
                  {emailUpdateStatus === 'success' && (
                    <div className="text-xs text-[#10B981] font-medium leading-relaxed px-1">
                      Check both <b>{currentUserEmail}</b> and <b>{newEmail}</b> for confirmation links.
                    </div>
                  )}
                  {emailUpdateStatus === 'error' && (
                    <div className="text-xs text-red-400 font-medium px-1">Failed to update email.</div>
                  )}
                  <div className="flex items-center gap-2 mt-1">
                    <button type="submit" disabled={emailUpdateStatus === 'loading' || emailUpdateStatus === 'success'}
                      className="flex-1 bg-white text-black text-xs font-bold tracking-tight py-2.5 rounded-lg hover:bg-zinc-200 transition-colors disabled:opacity-50">
                      {emailUpdateStatus === 'loading' ? 'Saving...' : 'Save'}
                    </button>
                    <button type="button" onClick={() => { setIsUpdatingEmail(false); setEmailUpdateStatus('idle'); setNewEmail(''); }}
                      className="flex-1 bg-zinc-800 border border-white/5 text-white text-xs font-bold tracking-tight py-2.5 rounded-lg hover:bg-zinc-700 transition-colors">
                      Cancel
                    </button>
                  </div>
                </form>
              )}
            </div>

            <div className="flex justify-center gap-4 mt-6 mb-4">
              <a href="https://gettermscdn.com/view/TeanA/privacy/en-us" target="_blank" rel="noopener noreferrer"
                className="text-[11px] font-medium text-zinc-500 hover:text-zinc-300 transition-colors">
                Privacy Policy
              </a>
              <a href="https://gettermscdn.com/view/TeanA/terms-of-service/en-us" target="_blank" rel="noopener noreferrer"
                className="text-[11px] font-medium text-zinc-500 hover:text-zinc-300 transition-colors">
                Terms of Service
              </a>
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setTimeout(() => {
                    const btn = document.querySelector('.asw-menu-btn');
                    if (btn) btn.click();
                  }, 50);
                }}
                className="text-[11px] font-medium text-zinc-500 hover:text-zinc-300 transition-colors outline-none cursor-pointer"
              >
                Accessibility
              </button>
              <a href="mailto:support@help.studiosign.app"
                className="text-[11px] font-medium text-zinc-500 hover:text-zinc-300 transition-colors">
                Support
              </a>
            </div>
          </div>

          <button onClick={handleSignOut}
            className="w-full mt-auto flex items-center justify-center gap-2 bg-red-500/10 hover:bg-red-500/20 border border-red-500/20 text-red-500 font-bold tracking-widest py-4 rounded-xl transition-all">
            <SafeIcon icon={FiLogOut} className="text-[18px]" />
            SIGN OUT
          </button>
        </div>
      </div>

      {showDisconnectModal && createPortal(
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowDisconnectModal(false)} />
          <div
            className="relative w-full max-w-[320px] bg-[#09090B] border border-white/10 rounded-3xl p-6 shadow-2xl flex flex-col transform transition-all scale-100"
            style={{ animation: 'tzSlideUpScale 0.2s cubic-bezier(0.16, 1, 0.3, 1)' }}
          >
            <h3 className="text-lg font-bold text-white text-center tracking-tight mb-3">Disconnect Calendar?</h3>
            <p className="text-sm text-zinc-400 text-center mb-6 leading-relaxed">
              Are you sure you want to disconnect? Auto-Pilot will be disabled and your displays will remain in their current state.
            </p>
            <div className="flex flex-col gap-3">
              <button
                onClick={() => {
                  updateSetting('is_calendar_connected', false);
                  setShowDisconnectModal(false);
                }}
                className="py-3.5 rounded-xl bg-red-500/10 hover:bg-red-500/20 border border-red-500/20 text-red-500 font-bold tracking-tight transition-all text-sm uppercase"
              >
                Disconnect
              </button>
              <button
                onClick={() => setShowDisconnectModal(false)}
                className="py-3 rounded-xl bg-zinc-900 border border-white/5 hover:bg-zinc-800 text-white font-semibold tracking-tight transition-all text-sm uppercase"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}